/**
 * The @nosto/web-components `DynamicCard` builds its markup fetch from the myshopify shop domain
 * (`https://{window.Shopify.shop}{routes.root}/products/...`). On a custom-domain storefront that
 * is a *different origin* than the page, and on secondary locales the myshopify URL 301-redirects
 * to the canonical domain — so the request is blocked by CORS and the card never loads.
 *
 * Rewriting just those product-card fetches to the current origin makes them same-origin: no CORS,
 * no redirect, and the correct locale is served directly. It also removes the need for the
 * locale-mismatch reload / recovery in the common case (those stay as a fallback).
 */

type ShopifyWindow = Window & { Shopify?: { shop?: string } }
type PatchedFetch = typeof fetch & { __nostoSameOriginCardFetch?: true }

/**
 * Rewrite a `DynamicCard` product-card fetch on the shop origin to the current origin.
 * Returns `null` when the URL must be left untouched (different origin, not a card fetch, or a
 * relative URL — which is already same-origin).
 */
export function rewriteCardFetchUrl(rawUrl: string, shopOrigin: string, targetOrigin: string): string | null {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return null
  }
  if (url.origin !== shopOrigin) return null
  if (!url.pathname.includes("/products/")) return null
  // Only the section-render fetches the web component issues — never cart/app/other shop calls.
  if (!url.searchParams.has("view") && !url.searchParams.has("section_id")) return null

  const target = new URL(targetOrigin)
  url.protocol = target.protocol
  url.host = target.host
  return url.toString()
}

/**
 * Install a `window.fetch` wrapper that redirects the web component's cross-origin product-card
 * fetches to the current origin. Idempotent, and a no-op when the shop origin already matches the
 * page origin (e.g. previewing directly on the *.myshopify.com domain).
 */
export function installSameOriginCardFetch(): void {
  const shop = (window as ShopifyWindow).Shopify?.shop
  if (!shop) return // web component already falls back to location.origin

  const shopOrigin = `https://${shop}`
  if (shopOrigin === window.location.origin) return

  if ((window.fetch as PatchedFetch).__nostoSameOriginCardFetch) return

  const inner = window.fetch.bind(window)
  const patched = ((input: RequestInfo | URL, init?: RequestInit) => {
    try {
      if (typeof input === "string" || input instanceof URL) {
        const rewritten = rewriteCardFetchUrl(input.toString(), shopOrigin, window.location.origin)
        if (rewritten) return inner(rewritten, init)
      } else if (input instanceof Request) {
        const rewritten = rewriteCardFetchUrl(input.url, shopOrigin, window.location.origin)
        if (rewritten) return inner(new Request(rewritten, input), init)
      }
    } catch {
      // Any unexpected input shape: fall back to the unmodified fetch.
    }
    return inner(input, init)
  }) as PatchedFetch
  patched.__nostoSameOriginCardFetch = true
  window.fetch = patched
}
